<template>
  <div class="discovery-library">
    <div class="page-head">
      <div class="head-actions">
        <el-radio-group v-model="platform" size="small" @change="onPlatformChange">
          <el-radio-button value="xiaohongshu">小红书</el-radio-button>
          <el-radio-button value="douyin">抖音</el-radio-button>
        </el-radio-group>
        <el-button :icon="Refresh" :loading="loading" @click="fetchNotes">刷新</el-button>
      </div>
    </div>

    <el-card shadow="never">
      <template #header>
        <div class="header-actions">
          <span>已保存 {{ total }} 篇</span>
          <el-input
            v-model="keyword"
            placeholder="搜索标题、作者、来源关键词"
            clearable
            style="width: 280px"
            @keyup.enter="onSearch"
            @clear="onSearch"
          >
            <template #append>
              <el-button :icon="Search" @click="onSearch" />
            </template>
          </el-input>
        </div>
      </template>

      <el-table :data="items" v-loading="loading" style="width: 100%">
        <el-table-column label="封面" width="88">
          <template #default="{ row }">
            <el-image
              v-if="row.cover_url"
              :src="row.cover_url"
              fit="cover"
              class="cover-thumb"
              referrerpolicy="no-referrer"
            />
            <span v-else class="text-muted">无图</span>
          </template>
        </el-table-column>
        <el-table-column prop="title" label="标题" min-width="220" show-overflow-tooltip />
        <el-table-column prop="author_name" label="作者" width="120" show-overflow-tooltip />
        <el-table-column label="互动" width="160">
          <template #default="{ row }">
            赞 {{ row.likes }} · 藏 {{ row.collects }} · 评 {{ row.comments }}
          </template>
        </el-table-column>
        <el-table-column prop="source_keyword" label="来源关键词" width="140" show-overflow-tooltip />
        <el-table-column label="流转" width="90">
          <template #default="{ row }">
            <el-tag size="small" type="info">{{ row.pipeline_status === 'drafted' ? '草稿' : '已采集' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="保存时间" width="170">
          <template #default="{ row }">{{ formatTime(row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="openDetail(row)">详情</el-button>
            <el-button link @click="$router.push('/create/rewrite/text')">去洗稿</el-button>
            <el-link
              v-if="row.note_url"
              :href="row.note_url"
              target="_blank"
              type="primary"
              style="margin-right: 8px"
            >原文</el-link>
            <el-popconfirm title="确定从发现库删除？" @confirm="removeNote(row)">
              <template #reference>
                <el-button link type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <el-empty v-if="!loading && !items.length" :description="emptyText">
        <el-button type="primary" @click="$router.push(discoverPath)">去发现</el-button>
      </el-empty>

      <div v-if="total > pageSize" class="pager">
        <el-pagination
          v-model:current-page="page"
          v-model:page-size="pageSize"
          :total="total"
          layout="total, prev, pager, next"
          @current-change="fetchNotes"
        />
      </div>
    </el-card>

    <el-drawer
      v-model="drawerVisible"
      :title="detail?.title || '详情'"
      :size="platform === 'douyin' ? '420px' : '480px'"
      class="platform-preview-drawer"
    >
      <PlatformContentViewer
        v-if="viewerItem"
        :item="viewerItem"
        :loading="detailLoading"
      />
      <div v-if="detail?.comments_json?.length" class="comments-block">
        <h4>已存评论 ({{ detail.comments_json.length }})</h4>
        <div
          v-for="c in detail.comments_json.filter(x => !x.parent_comment_id).slice(0, 20)"
          :key="c.comment_id"
          class="comment-line"
        >
          <strong>{{ c.user_name }}</strong> {{ c.content }}
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Search, Refresh } from '@element-plus/icons-vue'
import axios from 'axios'
import { PUBLISH_V2 } from '../utils/appBase'
import { publishV2Api } from '../api'
import PlatformContentViewer from '../components/platform/PlatformContentViewer.vue'
import { normalizeContent } from '../components/platform/normalizeContent'

const V2 = PUBLISH_V2
const route = useRoute()
const router = useRouter()

const platformFromQuery = () => {
  const q = String(route.query.platform || '')
  if (q === 'douyin') return 'douyin'
  if (route.path.includes('douyin')) return 'douyin'
  return 'xiaohongshu'
}

const platform = ref(platformFromQuery())
const items = ref([])
const total = ref(0)
const page = ref(1)
const pageSize = ref(20)
const keyword = ref('')
const loading = ref(false)
const drawerVisible = ref(false)
const detail = ref(null)
const detailLoading = ref(false)

const apiBase = computed(() => (
  platform.value === 'douyin'
    ? `${V2}/douyin/discovery/library`
    : `${V2}/xiaohongshu/discovery/library`
))

const discoverPath = computed(() => (
  platform.value === 'douyin' ? '/discover/douyin' : '/discover/xhs'
))

const emptyText = computed(() => (
  platform.value === 'douyin' ? '还没有保存的抖音作品' : '还没有保存的笔记'
))

const viewerItem = computed(() => {
  if (!detail.value) return null
  return normalizeContent(
    { ...detail.value, platform: platform.value },
    { platform: platform.value, proxyUrl: publishV2Api.coverProxyUrl },
  )
})

const formatTime = (iso) => {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('zh-CN')
  } catch {
    return iso
  }
}

const fetchNotes = async () => {
  loading.value = true
  try {
    const res = await axios.get(apiBase.value, {
      params: { page: page.value, page_size: pageSize.value, keyword: keyword.value.trim() },
    })
    items.value = res.data.items || []
    total.value = res.data.total || 0
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || '加载失败')
  } finally {
    loading.value = false
  }
}

const onSearch = () => {
  page.value = 1
  fetchNotes()
}

const onPlatformChange = () => {
  page.value = 1
  keyword.value = ''
  router.replace({ path: '/discover/library', query: { platform: platform.value } })
  fetchNotes()
}

const openDetail = async (row) => {
  drawerVisible.value = true
  detailLoading.value = true
  detail.value = row
  try {
    const res = await axios.get(`${apiBase.value}/${row.id}`)
    detail.value = res.data
  } catch {
    ElMessage.warning('详情加载失败，已显示列表数据')
  } finally {
    detailLoading.value = false
  }
}

const removeNote = async (row) => {
  try {
    await axios.delete(`${apiBase.value}/${row.id}`)
    ElMessage.success('已删除')
    fetchNotes()
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || '删除失败')
  }
}

watch(
  () => route.query.platform,
  () => {
    const next = platformFromQuery()
    if (next !== platform.value) {
      platform.value = next
      page.value = 1
      fetchNotes()
    }
  },
)

onMounted(fetchNotes)
</script>

<style scoped>
.discovery-library { padding: 4px 0 24px; }
.page-head {
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.head-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.page-title { margin: 0 0 4px; font-size: 18px; }
.page-desc { margin: 0; color: #909399; font-size: 13px; }
.header-actions { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; }
.cover-thumb { width: 56px; height: 56px; border-radius: 4px; }
.text-muted { color: #c0c4cc; font-size: 12px; }
.pager { margin-top: 16px; display: flex; justify-content: flex-end; }
.comments-block { margin-top: 16px; }
.comments-block h4 { margin: 0 0 8px; font-size: 14px; }
.comment-line { font-size: 12px; margin-bottom: 6px; line-height: 1.5; color: #606266; }
</style>
